import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles.module.scss';
import {
  MetricChartDotRadius,
  MetricChartDotSmall,
  MetricChartXAxisHeight,
  MetricChartYAxisWidth,
} from './constants/index.jsx';
import { NDVIPalette } from '../../constants/data';
import { MetricChartHeader } from './index.jsx';
import MetricChartTooltip from './MetricChartTooltip.jsx';
import { BreakpointMobile } from '../../constants/ui';
import { ChartColorsMap } from '../Charts/constants/index.jsx';

const ChartTooltip = ({ active, payload, sensor }) => {
  if (!active || !payload || payload.length < 1) return null;
  const data = payload[0].payload;
  const value = `${data.area[0]}...${data.area[1]}`;
  const valueCompared =
    payload.length > 2
      ? [
          { name: sensor.name, value: `${data.value}`, color: ChartColorsMap.ndvi },
          {
            name: sensor.name2,
            value: `${data.value2}`,
            color: ChartColorsMap.ndviCompared,
          },
        ]
      : null;
  return <MetricChartTooltip value={`${data.value} Avg`} comparedData={valueCompared} label={value} />;
};

const MetricChartNdvi = ({ title, data, sensor, widthViewport }) => {
  const { selectedSecondarySensor } = useSelector((state) => state.sensors);
  const [stopOffset, setStopOffset] = useState(null);

  // Gradient line
  useEffect(() => {
    const valuesFull = data.data.map((d) => d.value);
    const values = valuesFull.filter((stop, index, arr) => {
      return index === 0 || stop !== arr[index - 1];
    });

    if (values && values.length > 0) {
      const stops = [];

      if (values.length > 1) {
        const valueMin = Math.min(...values);
        const valueMax = Math.max(...values);
        const percent = (val) => (val - valueMin) / (valueMax - valueMin);

        NDVIPalette.map((item) => {
          const t = 100 - percent(item.offset) * 100;
          stops.push({
            color: item.color,
            value: Math.round(t),
          });
        });
      } else {
        const f = NDVIPalette.filter((item) => item.offset.toFixed(1) === Number(values[0]).toFixed(1));
        if (f && f.length > 0) {
          stops.push({
            color: f[0].color,
            value: 0,
          });
        }
      }

      setStopOffset(stops);
    }
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <div className={styles.metricChart}>
      {widthViewport > BreakpointMobile && (
        <MetricChartHeader
          title={title}
          sensorName={sensor.name}
          sensorNameCompared={sensor.name2}
          color={ChartColorsMap.ndvi}
          colorCompared={ChartColorsMap.ndviCompared}
        />
      )}

      <ResponsiveContainer width="100%" height={140} className="chart-report">
        <ComposedChart
          width={500}
          height={140}
          data={data.data}
          margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="gradientNdvi" x1="0" y1="0" x2="0" y2="1">
              {stopOffset && stopOffset.length > 0 ? (
                stopOffset.map((d, index) => <stop key={index} offset={`${d.value}%`} stopColor={d.color} />)
              ) : (
                <>
                  <stop offset="0%" stopColor={ChartColorsMap.ndvi} />
                  <stop offset="100%" stopColor={ChartColorsMap.ndvi} />
                </>
              )}
            </linearGradient>
          </defs>
          <XAxis dataKey="date" tickLine={false} height={MetricChartXAxisHeight} />
          <YAxis tickCount={3} tickSize={0} tickMargin={6} width={MetricChartYAxisWidth} domain={[0, 1]} />
          <Tooltip content={<ChartTooltip sensor={sensor} />} />
          <Area
            dataKey="area"
            type="monotone"
            stroke="none"
            fillOpacity={0.03}
            fill="#000"
            activeDot={MetricChartDotSmall}
          />
          <Line
            type="monotone"
            dataKey="value"
            strokeWidth={2}
            stroke={
              selectedSecondarySensor
                ? ChartColorsMap.ndvi
                : stopOffset && stopOffset.length > 1
                  ? 'url(#gradientNdvi)'
                  : stopOffset && stopOffset.length === 1
                    ? stopOffset[0].color
                    : ChartColorsMap.ndvi
            }
            activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.ndvi }}
          />
          {selectedSecondarySensor && data.data[0].value2 !== null && (
            <>
              <Area
                dataKey="area2"
                type="monotone"
                stroke="none"
                fillOpacity={0.03}
                fill="#000"
                activeDot={MetricChartDotSmall}
              />
              <Line
                type="monotone"
                dataKey="value2"
                strokeWidth={2}
                stroke={ChartColorsMap.ndviCompared}
                strokeDasharray="2 2"
                activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.ndviCompared }}
              />
            </>
          )}
          <CartesianGrid strokeDasharray="0 0" stroke={'var(--neutral-8)'} vertical={false} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className={styles.metricChartUnit}>0</div>
    </div>
  );
};

export default MetricChartNdvi;
